//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useContext } from 'react';
import { isBefore, isAfter, sub, isEqual } from 'date-fns';
import { parseDate, formatDate, sortDates } from '@/_utils/date';
import { selectAllDashboardData, selectCurrentCycle } from '@/redux/selectors/dashboardSelectors';
import { ConfigContext } from '@/contextAPI/configContext';
import { MatrixedChartValues, generateColorArrayFromConfigs } from '../../Reusable/chartUtils';
import { DashboardData } from '@/redux/slices/slice_types';
import DashboardWidget from '../../DashboardWidget';
import { PreBuiltGraphs } from '@/components/Dashboard/DefaultGraphs';
import { LineChartData } from '../../Reusable/LineChart';
import { useAppSelector } from '@/redux/hooks';
import { CustomCycleDate } from '@/__configs/config_types';

interface OnTimeDeliveryProps {
  title: string;
  caption: string;
}

function OnTimeDelivery({ title, caption }: OnTimeDeliveryProps) {
  const data = useAppSelector(selectAllDashboardData);
  const cycleDate = useAppSelector(selectCurrentCycle);
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const enabledDatasets = (dashboardConfig?.defaultGraphs && dashboardConfig.defaultGraphs['on-time-delivery']?.datasets) || {};

  const { otd70, otd100, prettyDates } = parseData(data, cycleDate);
  const otd70Label = enabledDatasets.otd70;
  const otd100Label = enabledDatasets.otd100;

  const buildLineChartData = () => {
    const values: MatrixedChartValues[] = [];
    if (otd70Label) values.push({ ...otd70, label: otd70Label });
    if (otd100Label) values.push({ ...otd100, label: otd100Label });

    const noData = !values.length || !values.every((v) => !v.data?.length);

    const colors = generateColorArrayFromConfigs(
      prettyDates.length,
      dashboardConfig.chartColors,
      dashboardConfig.graphSettings['on-time-delivery'] ? dashboardConfig.graphSettings['on-time-delivery'].colors : []
    );

    const lcData: LineChartData = {
      labels: prettyDates,
      values,
      colors: colors,
      id: PreBuiltGraphs.ON_TIME_DELIVERY,
      noDataMessage: noData ? null : 'No On Time Deliveries in the Last 6 months',
      legend: 'square' as const,
      axes: {
        x: 'Months',
        y: '% Completed'
      },
      ymax: 100,
      ttlabel: (context) => context.dataset.label + ': ' + context.dataset.data[context.dataIndex] + '%'
    };

    return lcData;
  };

  const graphData: LineChartData = buildLineChartData();

  return <DashboardWidget {...{ title }} {...{ caption }} data={graphData} type="line" />;
}

export default OnTimeDelivery;

function parseData(data: DashboardData, selectedCycle: string | CustomCycleDate) {
  const selectedCycleDate = selectedCycle !== 'All' && typeof selectedCycle !== 'object' ? parseDate(selectedCycle) : new Date();
  const cutoffDate = sub(selectedCycleDate, { months: 6 });

  const dates = sortDates(Object.keys(data), 'yyyy-MM-dd').filter((date) => {
    const parsedDate = parseDate(date);
    return isBefore(cutoffDate, parsedDate) && (isAfter(selectedCycleDate, parsedDate) || isEqual(selectedCycleDate, parsedDate));
  });

  const parsedOtd70 = parseOtd(data, dates, 'otd70');
  const parsedOtd100 = parseOtd(data, dates, 'otd100');
  const prettyDates = dates.map((date) => formatDate(date, 'MMM yyyy'));

  return { otd70: parsedOtd70, otd100: parsedOtd100, prettyDates };
}

function parseOtd(data: DashboardData, dates: string[], key: string) {
  const line: MatrixedChartValues = {
    label: key,
    data: []
  };

  dates.map((date) => {
    line.data.push(Math.round(data[date][key] * 100));
  });
  return line;
}
